import React, { useState, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@material-ui/core';
import { withAnalytic } from '@skooldio/analytics-provider/dist/Analytics/hoc';

import { BackdropProgress, Icon, ICON_LIST } from 'Components/Base';
import ButtonIcon from 'Components/ButtonIcon';
import ButtonOutlinedIcon from 'Components/ButtonOutlinedIcon';
import ExportAdaptiveQuizAttemptReportData from 'Domains/ExportAdaptiveQuizAttemptReportData';
import ExportAdaptiveQuizReportData from 'Domains/ExportAdaptiveQuizReportData';
import { useAnalytics } from 'Libs/Analytics';
import * as mixpanel from 'config/mixpanel';

const ExportButton = ({ disabled, onExport }) => {
  const { t } = useTranslation('adaptiveQuiz');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleExport = async () => {
    setLoading(true);
    try {
      await onExport();
    } catch (error) {
      setOpen(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Fragment>
      <BackdropProgress open={loading} />
      <ButtonOutlinedIcon
        leftIcon={<Icon name={ICON_LIST.EXPORT} />}
        lineHeight={'0.5'}
        textProp={{
          fontWeight: 'semibold',
        }}
        height={'40px'}
        message={t('EXPORT')}
        disabled={disabled || loading}
        onClick={handleExport}
      />
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{t('EXPORT_ERROR_TITLE')}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {t('EXPORT_ERROR_TEXT')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <ButtonIcon
            onClick={() => setOpen(false)}
            variant={'primary'}
            lineHeight={'1'}
            height={'40px'}
            message={t('SUBMIT')}
          />
        </DialogActions>
      </Dialog>
    </Fragment>
  );
};

const AdaptiveQuizExporter = ({ disabled, filterValue, schoolCode, onLogAction, myProfileId }) => {
  const { send } = useAnalytics();

  return (
    <ExportAdaptiveQuizReportData filterValue={filterValue} schoolCode={schoolCode}>
      {({ fetchAdaptiveQuizReportData, exportAdaptiveQuizReport }) => (
        <ExportButton
          disabled={disabled}
          onExport={async () => {
            onLogAction({
              category: 'Export',
              action: `Click`,
              label: `Click on adaptive quiz page`,
            });
            send('export', { on: 'adaptive_quiz_page' });
            mixpanel.trackEvent('Adaptive Quiz Export File Click', {
              distinct_id: myProfileId,
            });
            const { data, error } = await fetchAdaptiveQuizReportData();
            if (error) throw error;
            exportAdaptiveQuizReport({ report: data.foQuizFeedReport });
          }}
        />
      )}
    </ExportAdaptiveQuizReportData>
  );
};

const AdaptiveQuizAttemptExporter = ({
  disabled,
  filterValue,
  schoolCode,
  username,
  onLogAction,
  myProfileId,
}) => {
  const { send } = useAnalytics();

  return (
    <ExportAdaptiveQuizAttemptReportData
      filterValue={filterValue}
      schoolCode={schoolCode}
      username={username}
    >
      {({ fetchAdaptiveQuizAttemptReportData, exportAdaptiveQuizAttemptReport }) => (
        <ExportButton
          disabled={disabled}
          onExport={async () => {
            onLogAction({
              category: 'Export',
              action: `Click`,
              label: `Click on adaptive quiz individual page`,
            });
            send('export', { on: 'adaptive_quiz_individual_page' });
            mixpanel.trackEvent('Adaptive Quiz Individual Export File Click', {
              distinct_id: myProfileId,
            });
            const { data, error } = await fetchAdaptiveQuizAttemptReportData();
            if (error) throw error;
            exportAdaptiveQuizAttemptReport({ report: data.foQuizFeedAttemptReport });
          }}
        />
      )}
    </ExportAdaptiveQuizAttemptReportData>
  );
};

const AdaptiveQuizExporterWithAnalytic = withAnalytic(AdaptiveQuizExporter);
const AdaptiveQuizAttemptExporterWithAnalytic = withAnalytic(AdaptiveQuizAttemptExporter);

export {
  AdaptiveQuizExporterWithAnalytic as AdaptiveQuizExporter,
  AdaptiveQuizAttemptExporterWithAnalytic as AdaptiveQuizAttemptExporter,
};
